import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MailConfigService } from '@service/mail-config/mail-config.service';
import { SettingMailConfig } from '@model/setting-mail-config';
import { ToastrService } from 'ngx-toastr';
import { NgxPermissionsService } from 'ngx-permissions';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-mail-config-add',
  templateUrl: './mail-config-add.component.html',
  styleUrls: ['./mail-config-add.component.scss']
})
export class MailConfigAddComponent implements OnInit {
  title: string = "CONFIGURATION EMAIL"
  form: FormGroup;
  @Input() mailConfig: SettingMailConfig;
  edit: boolean = false;
  submit: boolean = false;
  testing: boolean = false;

  providers = [
    { value: 'SMTP', label: 'SMTP Generique' },
    { value: 'RESEND', label: 'Resend' },
    { value: 'MAILJET', label: 'Mailjet' },
    { value: 'AWS_SES', label: 'AWS SES' },
    { value: 'SENDGRID', label: 'SendGrid' },
    { value: 'BREVO', label: 'Brevo' }
  ];

  encryptions = [
    { value: 'TLS', label: 'TLS' },
    { value: 'SSL', label: 'SSL' },
    { value: 'NONE', label: 'Aucun' }
  ];

  constructor(
    private formBuild: FormBuilder,
    private mailConfigService: MailConfigService,
    private toastr: ToastrService,
    private permissionsService: NgxPermissionsService
  ) {
    const permission = JSON.parse(localStorage.getItem('permission-zen')) ? JSON.parse(localStorage.getItem('permission-zen')) : [];
    this.permissionsService.loadPermissions(permission);
    this.newForm();
  }

  ngOnInit(): void {
    this.editForm();
  }

  newForm() {
    this.form = this.formBuild.group({
      uuid: [null],
      provider: ['SMTP'],
      host: [null],
      port: [587],
      username: [null],
      password: [null],
      encryption: ['TLS'],
      apiKey: [null],
      apiSecret: [null],
      accessKey: [null],
      secretKey: [null],
      region: [null],
      senderEmail: [null, [Validators.required, Validators.email]],
      senderName: [null],
      active: [false],
    });
  }

  editForm() {
    if (this.mailConfig) {
      this.form.patchValue({ ...this.mailConfig });
    }
  }

  editConfig(edit: boolean) {
    this.edit = edit;
    this.title = edit ? "MODIFIER LA CONFIGURATION EMAIL" : "CONFIGURATION EMAIL";
    if (!this.edit) {
      this.editForm();
    }
  }

  onProviderChange() {
    const provider = this.form.get('provider').value;
    // Reset champs specifiques
    this.form.patchValue({
      host: null,
      port: provider === 'SMTP' ? 587 : null,
      username: null,
      password: null,
      encryption: provider === 'SMTP' ? 'TLS' : null,
      apiKey: null,
      apiSecret: null,
      accessKey: null,
      secretKey: null,
      region: null
    });
  }

  get selectedProvider(): string {
    return this.form.get('provider')?.value || 'SMTP';
  }

  get showSmtpFields(): boolean {
    return this.selectedProvider === 'SMTP';
  }

  get showApiKeyField(): boolean {
    return ['RESEND', 'MAILJET', 'SENDGRID', 'BREVO'].includes(this.selectedProvider);
  }

  get showApiSecretField(): boolean {
    return this.selectedProvider === 'MAILJET';
  }

  get showAwsFields(): boolean {
    return this.selectedProvider === 'AWS_SES';
  }

  get showUsernameField(): boolean {
    return this.selectedProvider === 'SMTP' || this.selectedProvider === 'BREVO';
  }

  onSubmit() {
    this.submit = true;
    if (this.form.get('senderEmail').invalid) {
      this.toast('L\'email expediteur est requis et doit etre valide', 'Attention !', 'warning');
      return;
    }

    this.mailConfigService.update(this.form.getRawValue()).subscribe(
      (res: any) => {
        this.submit = false;
        if (res?.status === 'success') {
          this.title = "CONFIGURATION EMAIL";
          this.edit = false;
          if (res.data) {
            this.mailConfig = res.data;
            this.editForm();
          }
          this.toast('Configuration sauvegardee avec succes', 'Succes', 'success');
        }
      },
      error => {
        this.submit = false;
        this.toast('Erreur lors de la sauvegarde', 'Erreur', 'error');
      }
    );
  }

  onTest() {
    this.testing = true;
    this.mailConfigService.testConnection().subscribe(
      (res: any) => {
        this.testing = false;
        Swal.fire({
          icon: 'success',
          title: 'Test reussi !',
          text: 'L\'email de test a ete envoye avec succes.',
          confirmButtonText: 'OK'
        });
      },
      error => {
        this.testing = false;
        Swal.fire({
          icon: 'error',
          title: 'Echec du test',
          text: error?.error?.message || 'Impossible d\'envoyer l\'email de test. Verifiez votre configuration.',
          confirmButtonText: 'OK'
        });
      }
    );
  }

  toast(msg, title, type): void {
    if (type === 'info') {
      this.toastr.info(msg, title);
    } else if (type === 'success') {
      this.toastr.success(msg, title);
    } else if (type === 'warning') {
      this.toastr.warning(msg, title);
    } else if (type === 'error') {
      this.toastr.error(msg, title);
    }
  }

  get f() { return this.form.controls; }
}
